import { z } from 'zod';

// ============================================
// Campaign Message Stats Schemas
// ============================================

export const campaignMessageStatsSchema = z.object({
  clientWaNumber: z.string(),
  status: z.string(),
  failureReason: z.string().nullable().optional(),
  timestamp: z.coerce.date(),
});

export const campaignStatsResponseSchema = z.object({
  messages: z.array(campaignMessageStatsSchema),
});

// Types
export type CampaignMessageStats = z.infer<typeof campaignMessageStatsSchema>;
export type CampaignStatsResponse = z.infer<typeof campaignStatsResponseSchema>;

// ============================================
// Campaign List: query + cursor-paginated response
// ============================================

// Optional date window + keyset pagination. Shared so the backend validates and
// the frontend builds the same query contract.
export const campaignListQuerySchema = z.object({
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be in YYYY-MM-DD format')
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'endDate must be in YYYY-MM-DD format')
    .optional(),
  cursor: z.string().optional(),
  // Upper bound matches the controller's MAX_LIMIT clamp. Larger pages let the
  // analytics view pull a whole date window in far fewer round-trips.
  limit: z.coerce.number().int().positive().max(10000).optional(),
});
export type CampaignListQuery = z.infer<typeof campaignListQuerySchema>;

/**
 * Cursor-paginated campaign list envelope. The campaign item shape is supplied
 * by the caller (the backend validates against its own campaignSchema), so the
 * `{ campaigns, nextCursor }` wrapper is defined once for both sides.
 */
export const campaignListResponseSchema = <T extends z.ZodTypeAny>(
  campaign: T,
) =>
  z.object({
    campaigns: z.array(campaign),
    nextCursor: z.string().nullable(),
  });

export type CampaignListResponse<C> = {
  campaigns: C[];
  nextCursor: string | null;
};
